/**
 * A könyvelői admin váza.
 *
 * AMI ITT VAN: belépés, a munkamenet, a cég-szűrő megjelenítése, a
 * billentyűzet-regiszter és a súgó. Minden szöveg `t()`-n megy.
 *
 * A HIÁNYLISTA MEGÉRKEZETT (#6, a tracer bullet 3. lépése) — a nyitóoldal
 * mostantól az, ami a termék állítása: „mit kell bekérnem?". A Munkalista és
 * a Tár még fül; azok a §8 3. és 5. lépése.
 *
 * A három felületnél több nem lesz: negyedik felület nincs, és a cégválasztó
 * nem navigáció, hanem SZŰRŐ. Ezt a különbséget a váz is kimondja.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PasswordInput } from '@/components/common';
import { Archive } from '@/features/archive/Archive';
import { useArchive } from '@/features/archive/useArchive';
import { useAuth } from '@/features/auth/AuthProvider';
import { readMagicLinkToken, SetPassword } from '@/features/auth/SetPassword';
import { CompanyPicker, CompanyTrigger } from '@/features/company/CompanyPicker';
import { useCompanyPicker } from '@/features/company/useCompanyPicker';
import { DiscrepancyList } from '@/features/discrepancies/DiscrepancyList';
import { useDiscrepancyList } from '@/features/discrepancies/useDiscrepancyList';
import { useDiscrepancySelection } from '@/features/discrepancies/useDiscrepancySelection';
import { ImpersonationNotice } from '@/features/impersonation/ImpersonationNotice';
import {
  useImpersonation,
  useImpersonationCommands,
} from '@/features/impersonation/useImpersonation';
import { NavCredentialsDialog } from '@/features/nav/NavCredentialsDialog';
import { NavSyncNotice } from '@/features/nav/NavSyncNotice';
import { useNavSync } from '@/features/nav/useNavSync';
import { CommandPalette } from '@/features/palette/CommandPalette';
import type { Command } from '@/features/palette/commands';
import { SettingsPage, settingsShortcuts } from '@/features/settings/SettingsPage';
import { TensoftRefreshNotice, TensoftStamp } from '@/features/tensoft/refresh/TensoftRefresh';
import { useTensoftRefresh } from '@/features/tensoft/refresh/useTensoftRefresh';
import { useTensoftBooking } from '@/features/worklist/tensoft/useTensoftBooking';
import { useWorklist } from '@/features/worklist/useWorklist';
import { Worklist } from '@/features/worklist/Worklist';
import { useDialogFocus } from '@/hooks/useDialogFocus';
import { type Shortcut, shortcutLabel, useShortcuts } from '@/hooks/useShortcuts';
import { ApiError } from '@/lib/api-client';
import { useSelectedCompany } from '@/lib/company-selection';

export default function App() {
  const { me, loading } = useAuth();
  const [magicToken, setMagicToken] = useState<string | null>(readMagicLinkToken);

  // A CÍMSORT RÖGTÖN KITAKARÍTJUK. A token a memóriában marad, amíg a
  // képernyő él — a megosztható címben viszont nincs keresnivalója: egy
  // megmutatott vagy elmentett cím különben belépést érne.
  useEffect(() => {
    if (magicToken !== null && window.location.hash !== '') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [magicToken]);

  // AMINT VAN MUNKAMENET, A TOKEN TÁRGYTALAN. Nélküle a KILÉPÉS után a
  // jelszóállító képernyő jönne vissza egy már elhasznált linkkel, és a
  // kolléga első látványa a „nem érvényes" mondat lenne.
  useEffect(() => {
    if (me !== null) {
      setMagicToken(null);
    }
  }, [me]);

  const forgetToken = useCallback(() => {
    setMagicToken(null);
  }, []);

  /**
   * A CÉGVÁLTÁS ÚJRAÉPÍTI A VÁZAT (#73) — a `key` miatt.
   *
   * Minden lista a választott cégre kér, és egy váltás után a régi cég
   * útközben lévő válasza nem érkezhet meg az új cég képernyőjére. A teljes
   * újraépítés ezt egyszerre zárja ki az összes horognál, ahelyett hogy
   * mindegyik külön figyelné a választást.
   *
   * A FELÜLET ITT ÉL, a váz fölött: a váltás után a könyvelő ott marad, ahol
   * volt — a cégválasztó szűrő, nem navigáció.
   */
  const company = useSelectedCompany();
  const [surface, setSurface] = useState<Surface>('discrepancies');

  if (loading) {
    return null;
  }
  // A SORREND SZÁMÍT: aki már be van lépve, annak a link tárgytalan.
  if (me) {
    return <Shell key={company ?? 'all'} surface={surface} onSurface={setSurface} />;
  }
  return magicToken === null ? (
    <SignIn />
  ) : (
    <SetPassword token={magicToken} onGiveUp={forgetToken} />
  );
}

function SignIn() {
  const { t } = useTranslation();
  const { signIn, signedOutReason } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (caught) {
      // A szerver üzenete magyarul jön; a saját szövegünk csak akkor kell,
      // ha nem érkezett semmi (hálózati hiba).
      const message = caught instanceof ApiError && caught.message ? caught.message : null;
      setError(message ?? t('auth.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="centered">
      <form className="card" onSubmit={onSubmit}>
        <h1>{t('auth.signInTitle')}</h1>

        {/* MIÉRT VAN ITT. Aki lejárt munkamenettel esik vissza a belépőre, szó
            nélkül azt hiszi, ő rontott el valamit — és legközelebb tétovábban
            nyúl a rendszerhez. Egy mondat elveszi ezt. Azt viszont NEM mondjuk
            meg, mi járt le pontosan és mikor: a felhasználónak egy teendője
            van, és a többi a rendszer dolga. */}
        {/* `<output>` és nem `<p role="status">`: ugyanaz az élő régió, de a
            szerep a MEGFELELŐ ELEMBŐL jön, nem egy rátett attribútumból. */}
        {signedOutReason === 'expired' ? (
          <output className="notice">{t('auth.expired')}</output>
        ) : null}

        <label htmlFor="email">{t('auth.email')}</label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          // A billentyűzet-első felület a fókusszal kezd, nem az egérrel.
          // biome-ignore lint/a11y/noAutofocus: belépőképernyő, egyetlen űrlap
          autoFocus
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <label htmlFor="password">{t('auth.password')}</label>
        <PasswordInput
          id="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" disabled={busy}>
          {t('auth.submit')}
        </button>
      </form>
    </main>
  );
}

type Surface = 'discrepancies' | 'worklist' | 'archive';

/** A három felület címe és kérdése — a szójegyzék „A három felület" táblája. */
const SURFACE_TITLES: Record<Surface, { title: string; subtitle: string }> = {
  discrepancies: { title: 'discrepancyList.title', subtitle: 'discrepancyList.subtitle' },
  worklist: { title: 'worklist.title', subtitle: 'worklist.subtitle' },
  archive: { title: 'archive.title', subtitle: 'archive.subtitle' },
};

function Shell({ surface, onSurface }: { surface: Surface; onSurface: (to: Surface) => void }) {
  const { t } = useTranslation();
  const { me, signOut } = useAuth();
  const [helpOpen, setHelpOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  /** A NAV-kulcsbevitel ablaka (IV/D3) — a palettáról nyílik, nem menüből. */
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  /**
   * A BEÁLLÍTÁSOK LAP (#25) — a fejlécből nyílik, a Kilépés mellől.
   *
   * ⚠️ ÁLLAPOT, NEM NEGYEDIK `surface`. A `surface` a HÁROM munkaképernyő
   * tengelye, és az zárt (`CLAUDE.md` §2). A beállítások nem azon a tengelyen
   * van: nem munka, hanem adminisztráció — ezért külön kapcsoló, és ezért
   * TAKARJA a fülsávot, ahelyett hogy bővítené.
   */
  const [settingsOpen, setSettingsOpen] = useState(false);
  const discrepancies = useDiscrepancyList();
  const selection = useDiscrepancySelection(discrepancies);
  const worklist = useWorklist();
  /** A TÁR (#74) — csak akkor kér, ha látszik: ritka felület, nem háttérmunka. */
  const archive = useArchive(surface === 'archive');
  /**
   * A LEFUTOTT KÖR UTÁN MAGÁTÓL FRISSÜL A LISTA (#37).
   *
   * MINDKETTŐ FRISSÜL, nem csak az, amelyiken épp állunk. A NAV-menet végén a
   * párosítás fut le, és az KÉT dolgot vezet át: nyit vagy zár hiánytételeket
   * (hiánylista), ÉS párosított állapotba billenti a hozzájuk tartozó
   * bizonylatokat (munkalista). Csak a látható felületet frissítve a másik
   * néma módon állna elavultan — és a könyvelő a felületváltáskor sem tudná
   * meg, hogy amit lát, egy köre elavult.
   *
   * ⚠️ CSAK A KÖR VÉGÉN HÍVÓDIK, menet közben soha. A `useNavSync` a `done`
   * ágon hívja, a `stalled`-en nem: félig lefutott körre frissíteni annyi,
   * mint félkész állapotot késznek mutatni.
   */
  const reloadAfterSync = useCallback(() => {
    discrepancies.reload();
    worklist.reload();
  }, [discrepancies.reload, worklist.reload]);

  /**
   * A KÉZI NAV-SZINKRON A VÁZBAN ÉL, nem a hiánylistában — a paletta mindkét
   * felületről elérhető (`Ctrl+K`), tehát a visszajelzésének is mindkettőn
   * látszania kell. A hiánylistába zárva a munkalistáról indított szinkron
   * NÉMÁN futna le.
   */
  const navSync = useNavSync(reloadAfterSync);

  /**
   * MELYIK FELÜLETEN ÁLLUNK — kettő a háromból, a Tár még nincs.
   *
   * NEM ÚTVONAL, ÁLLAPOT. Router itt nem indokolt: a `CLAUDE.md` §2 szerint
   * három felület van és negyedik nincs, tehát a halmaz ZÁRT. Egy útválasztó
   * a mélyhivatkozást hozná be, ami viszont a cégválasztóval együtt értelmes
   * — az pedig SZŰRŐ, nem navigáció, és a szerver kontextusából jön.
   *
   * Az állapot a váz FÖLÖTT él (`App`), hogy a cégváltás újraépítése után is
   * megmaradjon (#73).
   */

  // A „VALÓDI KONTÍR" ITT ÉL, nem a panelben: a kétes kötés `1`–`9`/`0`-ja a
  // közös regiszterbe kerül, és a regiszter itt van (`useTensoftBooking.ts`).
  const tensoft = useTensoftBooking(worklist.focusedId, surface === 'worklist');
  // FRISS LISTA, FRISS JELVÉNYEK: a helyben levett „kétes kötés" jelvényeket
  // a szerver jelzése váltja — de csak a lista kérése ELŐTT hozott döntésekét
  // (`useTensoftBooking` → `decided`).

  /**
   * A FRISSÍTÉS GOMB (#54) — a vázban, mert a fejléce mindkét felületen áll.
   * A megérkezett adatra a munkalista CSENDBEN frissül (Viktor, 10-05:
   * „magától"): a lista helyben marad, a kijelölt bizonylat is, és a jobb
   * hasáb „Valódi kontír" panelje újra kéri a magáét.
   */
  const refreshWorklist = worklist.refresh;
  const reloadTensoftPanel = tensoft.reload;
  const tensoftArrived = useCallback(() => {
    refreshWorklist();
    reloadTensoftPanel();
  }, [refreshWorklist, reloadTensoftPanel]);
  const tensoftRefresh = useTensoftRefresh(tensoftArrived);

  const pruneTensoftDecided = tensoft.pruneDecided;
  useEffect(() => {
    if (worklist.loadedSince !== null) pruneTensoftDecided(worklist.loadedSince);
  }, [worklist.loadedSince, pruneTensoftDecided]);

  /**
   * A FELÜLET ELHAGYÁSA EGY HELYEN.
   *
   * A füleken kívül most a `Ctrl+1`/`Ctrl+2` is válthat, és a 2.6/22 zára
   * (elintézetlen bukással nem lehet elhagyni a munkalistát) MINDKETTŐRE
   * kell. Két helyen leírva az egyik előbb-utóbb kimaradna — és pont az a
   * billentyűs út lenne, amit senki nem próbál végig.
   */
  const leave = useCallback(
    (to: Surface) => {
      if (to !== 'worklist' && worklist.blocked) {
        return;
      }
      onSurface(to);
    },
    [worklist.blocked, onSurface]
  );

  /**
   * A CÉGVÁLASZTÓ (#73) — `c`, a fejléc gombja, vagy a paletta. A 2.6/22
   * zárát ő is tartja: elintézetlen bukással a cégváltás is elhagyás.
   */
  const picker = useCompanyPicker(me, worklist.blocked);
  /**
   * BELÉPÉS ÜGYFÉLKÉNT (#76) — a kiválasztott cég nevében. A `Shell` a
   * választásra van kulcsolva, tehát itt mindig az érvényes cég áll.
   */
  const selectedCompany = useSelectedCompany();
  const impersonation = useImpersonation(me);
  const impersonationCommands = useImpersonationCommands(
    impersonation,
    selectedCompany,
    picker.open
  );

  // EGY REGISZTER, a képernyő rövidítéseivel együtt. A súgó ebből épül, tehát
  // a lista ↑↓-je is megjelenik benne — egy második `useShortcuts` hívás a
  // listában kettéhasítaná a regisztert, és a súgó pont attól veszítené el az
  // értékét, amiért van.
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  /**
   * A LAP NYITOTT RÉTEGE — `useSettingsLayer` írja. Az `Escape` előbb ezt
   * zárja, és csak ha nincs, a lapot. Ref, nem állapot: egy űrlap nyitása nem
   * építheti újra a regisztert.
   */
  const settingsLayer = useRef<(() => void) | null>(null);
  const setSettingsLayer = useCallback((close: (() => void) | null) => {
    settingsLayer.current = close;
  }, []);
  const escapeSettings = useCallback(() => {
    // ⚠️ A SÚGÓ A LEGFELSŐ RÉTEG. Alatta egy félig kitöltött űrlap is állhat,
    // amit a könyvelő épp nem lát — az `Escape` előbb a súgót zárja (review).
    if (helpOpen) {
      setHelpOpen(false);
      return;
    }
    if (settingsLayer.current) {
      settingsLayer.current();
      return;
    }
    closeSettings();
  }, [helpOpen, closeSettings]);

  const shortcuts = useMemo<Shortcut[]>(
    () =>
      settingsOpen
        ? // ⚠️ A LAPON A MUNKA RÖVIDÍTÉSEI NEM ÉLNEK — sem a listáké, sem a
          // `Ctrl+1`/`Ctrl+2`. Nem feledékenységből: a két felületváltó a
          // fülsávra mutatna, ami ilyenkor nem is látszik, a `↑↓` pedig egy
          // nem renderelt listát mozgatna. A `useShortcuts` MINDEN találatnál
          // `preventDefault()`-ot hív, tehát a lapgörgetést is elvennénk a
          // billentyűzettől — „nem csinál semmit, és a böngészőt is
          // megbénítja".
          //
          // A paletta és a súgó MEGMARAD: mindkettő a lapról is értelmes.
          [
            ...settingsShortcuts(escapeSettings),
            {
              key: 'k',
              ctrl: true,
              labelKey: 'shortcut.palette',
              run: () => setPaletteOpen(true),
            },
            { key: '?', labelKey: 'shortcut.help', run: () => setHelpOpen(true) },
          ]
        : [
            // CSAK A LÁTHATÓ FELÜLET RÖVIDÍTÉSEI ÉLNEK. Mindkettő bejegyezve a ↑↓
            // két listát mozgatna egyszerre, és az `Enter` két különböző dolgot
            // jelentene — a `useShortcuts` az ELSŐ találatnál lefut, tehát a
            // sorrend döntené el, melyiket. Az a fajta rejtett szabály, amit a
            // 2.6/16 kizár.
            ...surfaceShortcuts(surface, {
              discrepancies: [...discrepancies.shortcuts, ...selection.shortcuts],
              worklist: [...worklist.shortcuts, ...tensoft.shortcuts],
              archive: archive.shortcuts,
            }),
            // A PALETTA ÉS A FELÜLETVÁLTÁS GLOBÁLIS (D9). Mindkettő módosítós, tehát
            // beviteli mezőben is él — a `Ctrl+K` nem betű, a könyvelő gépelés
            // közben is elérheti.
            { key: 'k', ctrl: true, labelKey: 'shortcut.palette', run: () => setPaletteOpen(true) },
            {
              key: '1',
              ctrl: true,
              labelKey: 'shortcut.toDiscrepancies',
              run: () => leave('discrepancies'),
            },
            { key: '2', ctrl: true, labelKey: 'shortcut.toWorklist', run: () => leave('worklist') },
            { key: '3', ctrl: true, labelKey: 'shortcut.toArchive', run: () => leave('archive') },
            { key: '?', labelKey: 'shortcut.help', run: () => setHelpOpen(true) },
            // MINDKÉT FELÜLETEN — a fejléc gombja is ott áll. A `t` szabad volt
            // mindkettőn (10-05); teljesítés alatt a horog maga nem küld újat.
            { key: 't', labelKey: 'shortcut.tensoftRefresh', run: tensoftRefresh.request },
            ...picker.shortcuts,
            {
              key: 'Escape',
              labelKey: 'shortcut.close',
              allowInInput: true,
              /**
               * EGY `Escape` EGY LÉPÉS VISSZA — és a teljes képernyő is egy lépés.
               *
               * MIÉRT NEM BÍZZUK A BÖNGÉSZŐRE. A böngésző magától is kilép `Esc`-re,
               * DE a `useShortcuts` minden találatnál `preventDefault()`-ot hív, és
               * hogy ez a kilépést megfogja-e, böngészőfüggő — Wayland alatt valódi
               * billentyűt injektálni nem tudtunk, tehát MÉRNI sem. Az „Eredeti"
               * panel gombja viszont KIÍRJA, hogy „Kilépés: Esc": egy ígéret, amit
               * nem ellenőriztünk, előbb-utóbb hazugság. Így a miénk.
               *
               * ELŐBBRE VALÓ A SÚGÓ ZÁRÁSÁNÁL: teljes képernyőn a súgó nem is
               * látszik, tehát azt csukni értelmetlen — a könyvelő a PAPÍRBÓL akar
               * kilépni. Egy leütés, egy réteg.
               */
              run: () => {
                if (document.fullscreenElement) {
                  void document.exitFullscreen?.().catch(() => undefined);
                  return;
                }
                setHelpOpen(false);
              },
            },
          ],
    [
      settingsOpen,
      escapeSettings,
      surface,
      discrepancies.shortcuts,
      selection.shortcuts,
      worklist.shortcuts,
      tensoft.shortcuts,
      archive.shortcuts,
      leave,
      tensoftRefresh.request,
      picker.shortcuts,
    ]
  );

  /**
   * A PALETTA PARANCSAI — abból, ami VAN.
   *
   * A D9 az ügyfélváltást és az időszakot is ide sorolja. Az ügyfélváltás
   * megvan (#73); időszak-szűrő még egyik listán sincs, ezért az nincs itt —
   * helyőrzőként olyan műveletet ígérne, ami nem történik meg.
   *
   * A `hint` a KÖZVETLEN billentyűt írja ki, ha van. Így a paletta egyben
   * tanítja is a kiosztást: aki a listából választ, legközelebb már tudja a
   * gyorsabb utat.
   */
  const commands = useMemo<Command[]>(
    () => [
      {
        id: 'to-discrepancies',
        labelKey: 'palette.toDiscrepancies',
        hint: 'Ctrl+1',
        run: () => leave('discrepancies'),
      },
      {
        id: 'to-worklist',
        labelKey: 'palette.toWorklist',
        hint: 'Ctrl+2',
        run: () => leave('worklist'),
      },
      {
        id: 'to-archive',
        labelKey: 'palette.toArchive',
        hint: 'Ctrl+3',
        run: () => leave('archive'),
      },
      {
        id: 'reload',
        labelKey: 'palette.reload',
        run: () => (surface === 'worklist' ? worklist.reload() : discrepancies.reload()),
      },
      /**
       * A SZINKRONIZÁLÁS MŰVELET, NEM OLDAL (`CLAUDE.md` §2) — és a
       * `commands.ts` fejléce szerint a paletta „az a hely, ahova a ritka
       * műveletek mennek". Nincs közvetlen billentyűje: a napi munkában
       * ritkán kell elővenni, az ütemező magától fut.
       */
      { id: 'nav-sync', labelKey: 'palette.navSync', run: () => navSync.run() },
      {
        id: 'tensoft-refresh',
        labelKey: 'palette.tensoftRefresh',
        hint: 't',
        run: () => tensoftRefresh.request(),
      },
      /**
       * A KULCSBEVITEL IS MŰVELET, NEM OLDAL (IV/D3).
       *
       * ⚠️ ÉS NINCS KÖZVETLEN BILLENTYŰJE, SZÁNDÉKOSAN. A `hint` a napi
       * munkában gyakori műveleteket tanítja; ez cégenként EGYSZER történik
       * meg egy ~80 elemű életciklusban. Egy kiosztott betű itt elvenne egyet
       * a napi munkától, és a súgó egy sorral hosszabb lenne olyasmiért,
       * amit senki nem gépel be fejből.
       */
      {
        id: 'nav-credentials',
        labelKey: 'palette.navCredentials',
        run: () => setCredentialsOpen(true),
      },
      ...picker.commands,
      ...impersonationCommands,
      { id: 'help', labelKey: 'palette.help', hint: '?', run: () => setHelpOpen(true) },
      { id: 'sign-out', labelKey: 'palette.signOut', run: () => signOut() },
    ],
    [
      leave,
      surface,
      worklist.reload,
      discrepancies.reload,
      navSync.run,
      tensoftRefresh.request,
      signOut,
      picker.commands,
      impersonationCommands,
    ]
  );

  /**
   * A REGISZTER EGY, AZ ÉLESÍTÉS VISZONT ÁLLAPOTFÜGGŐ.
   *
   * A súgó a TELJES regisztert mutatja — különben azt hazudná, hogy a felület
   * kevesebbet tud. De amíg a párbeszéd nyitva van, csak az `Escape` élhet: a
   * `useShortcuts` a találatnál `preventDefault()`-ot hív és lefuttatja a
   * `run()`-t, tehát egy nyíl a MODAL MÖGÖTT mozgatná a valódi fókuszt egy
   * sorra. Egy `aria-modal` párbeszéd mögé kiszökő fókusz a képernyőolvasónak
   * és a billentyűzetnek egyaránt hazugság.
   */
  const armed = useMemo(() => {
    // A PALETTA A SAJÁT BILLENTYŰIT MAGA VISZI (a mezőn, `stopPropagation`-nel),
    // tehát amíg nyitva van, a globális regiszterből SEMMI nem élhet — még az
    // `Escape` sem, mert azt is ő kezeli. Egy itt hagyott `Escape` a palettát
    // ÉS a súgót is csukná, egyetlen leütésre.
    if (paletteOpen) {
      return [];
    }
    // ⚠️ A KULCSBEVITEL ABLAKA UGYANÍGY ELNYELI A REGISZTERT. Beviteli
    // mezőkben áll a fókusz, félig beírt NAV-kulcsokkal: egy `Ctrl+1`
    // átnavigálna a hiánylistára, és a beírt titkok elvesznének. Az `Escape`
    // sem élhet, mert azt az ablak maga kezeli (`stopPropagation`) — kettő
    // közülük egyszerre a súgót is csukná.
    if (credentialsOpen) {
      return [];
    }
    // A cégválasztó ugyanúgy maga viszi a billentyűit, mint a paletta.
    if (picker.isOpen) {
      return [];
    }
    // A SÚGÓ ALATT CSAK A SÚGÓT ZÁRÓ `Esc` ÉL — egy leütés, egy réteg. A
    // felületek saját `Esc`-e (a Tár megnyitott bizonylata) előrébb áll a
    // regiszterben, és az első találat futna: a súgó alatt csukná be a papírt.
    return helpOpen
      ? [
          {
            key: 'Escape',
            labelKey: 'shortcut.close',
            allowInInput: true,
            run: () => setHelpOpen(false),
          },
        ]
      : shortcuts;
  }, [shortcuts, helpOpen, paletteOpen, credentialsOpen, picker.isOpen]);
  useShortcuts(armed);

  if (!me) {
    return null;
  }

  return (
    <div className="shell">
      <header>
        <strong>{t('app.name')}</strong>
        <span className="spacer" />
        <TensoftStamp refresh={tensoftRefresh} />
        <span>{t('app.signedInAs', { name: me.actor.name })}</span>
        {/* SZÁNDÉKOSAN burkolva: az `onClick={signOut}` a `MouseEvent`-et adná
            át indoknak, és a felhasználó saját kilépése „rendszer-kiléptetés"
            lenne. A fordító most szól érte — ezért van a paraméter típusozva
            uniónak, nem `string`-nek. */}
        {/* ⚠️ CSAK `ADMIN`-NAK LÁTSZIK — és ez KÉNYELEM, nem védelem. A
            kikényszerítés a szerveré (`@Roles('ADMIN')` a
            `CompanyController`-en); ez itt azt intézi, hogy az `ACCOUNTANT`
            ne kattintson rá egy 403-ba. A kettő nem cserélhető fel: felületi
            rejtésre biztonságot építeni a 2.6/23 bukásmódja. */}
        {me.actor.role === 'ADMIN' ? (
          <button type="button" aria-pressed={settingsOpen} onClick={() => setSettingsOpen(true)}>
            {t('app.settings')}
          </button>
        ) : null}
        <button type="button" onClick={() => signOut()}>
          {t('app.signOut')}
        </button>
      </header>

      {settingsOpen ? (
        /* ⚠️ A FÜLSÁV ÉS A MUNKA HELYÉT VESZI ÁT, nem melléjük kerül (#25,
           Viktor 2026-09-11, artboardon eldöntve). A három munkaképernyő
           marad három: amíg a lap áll, egyik sem elérhető, és a fülsáv
           helyén a MEGNEVEZETT visszaút áll. A másik jelölt egy ablak volt a
           munka fölött — az a négy szelet tábláinak szűk. */
        <SettingsPage onClose={closeSettings} onLayer={setSettingsLayer} />
      ) : (
        <>
          <nav aria-label={t('app.name')}>
            {/* A három felület — és nincs negyedik. A Tár még nem épült meg,
              ezért az NEM gomb: egy kattintható, de sehova nem vivő fül
              rosszabb, mint egy láthatóan még nem kész. */}
            {/* ELINTÉZETLEN BUKÁSSAL NEM LEHET ELHAGYNI A MUNKALISTÁT (2.6/22,
              3. pont). A tiltás nem néma: a bukott mentések sávja ott áll a
              lista tetején, és kimondja, hogy amíg nincs elintézve, nincs
              továbblépés — a magyarázat ezért az OKA mellett van, nem egy
              elérhetetlen buboréktippben a füleken. */}
            <button
              type="button"
              className={surface === 'discrepancies' ? 'tab active' : 'tab'}
              aria-current={surface === 'discrepancies'}
              disabled={surface === 'worklist' && worklist.blocked}
              onClick={() => leave('discrepancies')}
            >
              {t('nav.discrepancies')}
            </button>
            {/* A BUKÁS A MÁSIK FELÜLETRŐL IS LÁTSZIK. A `useWorklist` itt, a
              `Shell`-ben él, tehát az elintézetlen bukás ÁLLAPOTA túléli a
              felületváltást — csak a sávja tűnik el a `<main>`-nel együtt. Egy
              futó mentés a váltás után is visszajöhet hibával, és a 2.6/22
              harmadik pontja szerint azt nem szabad elnyelni: a fülre tett szám
              az egyetlen hely, ahol innen is megszólal.
              *(Code review fogta meg, 09-03.)* */}
            <button
              type="button"
              className={surface === 'worklist' ? 'tab active' : 'tab'}
              aria-current={surface === 'worklist'}
              onClick={() => leave('worklist')}
            >
              {t('nav.worklist')}
              {worklist.failed.length > 0 ? (
                <span className="tab__alert">
                  {t('nav.worklistFailed', { count: worklist.failed.length })}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              className={surface === 'archive' ? 'tab active' : 'tab'}
              aria-current={surface === 'archive'}
              onClick={() => leave('archive')}
            >
              {t('nav.archive')}
            </button>
          </nav>

          <main>
            {/* A VÁZ SZINTJÉN, A FELÜLET FÖLÖTT: a művelet globális, tehát a
              visszajelzése is az. A `<main>`-en BELÜL van, hogy a felületváltás
              ne dobja ki a szöveget a képernyőolvasó élő régiójából. */}
            <NavSyncNotice sync={navSync} />
            <TensoftRefreshNotice refresh={tensoftRefresh} />
            <ImpersonationNotice impersonation={impersonation} />

            {/* A FELÜLET A KULCS, nem egymásba ágyazott feltétel: három van,
                negyedik nincs. A cégválasztó mindhármon ugyanott áll (#73). */}
            <h1>{t(SURFACE_TITLES[surface].title)}</h1>
            <p className="subtitle">{t(SURFACE_TITLES[surface].subtitle)}</p>

            <CompanyTrigger picker={picker} />

            {
              {
                discrepancies: <DiscrepancyList state={discrepancies} selection={selection} />,
                worklist: (
                  <Worklist state={worklist} discrepancies={discrepancies} tensoft={tensoft} />
                ),
                archive: <Archive state={archive} onPickCompany={picker.open} />,
              }[surface]
            }

            <p className="hint">{t('shortcut.hint', { keys: '?' })}</p>
          </main>
        </>
      )}

      {helpOpen ? <Help shortcuts={shortcuts} onClose={() => setHelpOpen(false)} /> : null}
      {paletteOpen ? (
        <CommandPalette commands={commands} onClose={() => setPaletteOpen(false)} />
      ) : null}
      {credentialsOpen ? <NavCredentialsDialog onClose={() => setCredentialsOpen(false)} /> : null}
      <CompanyPicker picker={picker} />
    </div>
  );
}

/**
 * CSAK A LÁTHATÓ FELÜLET RÖVIDÍTÉSEI ÉLNEK — a felület a kulcs, nem egy
 * egymásba ágyazott feltétel. Három felület van, és negyedik nincs.
 */
function surfaceShortcuts(surface: Surface, bySurface: Record<Surface, Shortcut[]>): Shortcut[] {
  return bySurface[surface];
}

/**
 * A súgó a REGISZTERBŐL épül, tehát nem tud elavulni.
 *
 * A FÓKUSZ BEJÖN, BENT MARAD, ÉS VISSZAMEGY (#16/2) — a `useDialogFocus`
 * tartja meg azt, amit az `aria-modal` ígér. A `?`-t a könyvelő a munka
 * közben üti le; ha a fókusz a súgó mögött marad, a következő `Tab` egy
 * letakart gombra visz, és a felolvasó azt mondja be.
 */
function Help({ shortcuts, onClose }: { shortcuts: Shortcut[]; onClose: () => void }) {
  const { t } = useTranslation();
  const dialog = useDialogFocus<HTMLDivElement>();
  return (
    <div className="overlay">
      {/* A SZEREP A KÁRTYÁN VAN, NEM A RÉTEGEN. A csapda határa a doboz — a
          `role="dialog"` és a csapda ugyanarra az elemre kell kerüljön,
          különben a képernyőolvasó és a `Tab` két külön dolgot nevez
          párbeszédnek. */}
      <div
        className="card"
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label={t('shortcut.help')}
      >
        <h2>{t('shortcut.help')}</h2>
        <dl>
          {shortcuts.map((shortcut) => (
            // A KULCS ÉS A FELIRAT IS A MÓDOSÍTÓS ALAK. Nyers `key`-jel a
            // `Ctrl+K` és egy puszta `k` ugyanaz a React-kulcs lenne, a
            // súgóban pedig két azonos sor állna.
            <div key={shortcutLabel(shortcut)}>
              <dt>
                <kbd>{shortcutLabel(shortcut)}</kbd>
              </dt>
              <dd>{t(shortcut.labelKey)}</dd>
            </div>
          ))}
        </dl>
        <button type="button" onClick={onClose}>
          {t('shortcut.close')}
        </button>
      </div>
    </div>
  );
}
